import React, { useMemo, useCallback, useState } from 'react';
import SessionTimelinePeriod from './SessionTimelinePeriod';
import { Period } from '../SessionPeriods';
import { secondsToHms } from '../../Util/TimeInputField';
import { Reference, SessionEvent, timeSpanToSeconds } from '../../../services/sessionModels';
import SessionTimelineEvent from './SessionTimelineEvent';
import { FrameRange } from '../Label/SessionLabelTask';
import { Coordinate } from '../../VideoPlayer/VideoTypes';
import { Flag } from '../Label/useValidationFlags';
import TimelineFlag from './TimelineFlag';

export enum RangeType {
  Banner,
}

export type RangeGraphics = {
  ranges: FrameRange[];
  color: string;
  rangeType: RangeType;
};

export type MarkerGraphics = {
  timestamps: number[];
  color: string;
};

type Props = {
  playTime: number;
  fps?: number;
  seekToPlayTime: (time: number, clampTime: boolean) => void;
  videoDuration: number;
  teamHome?: Reference | undefined | null;
  teamAway?: Reference | undefined | null;
  periods?: Period[];
  events?: SessionEvent[];
  ranges?: RangeGraphics[];
  timeMarkers?: MarkerGraphics[];
  flags?: Flag[];
  initialGameState?: boolean;
  gameStateTransitions?: number[];
  startTimeOffset?: number;
  slidingWindow?: boolean;
  lastLabeledTime?: number;
  lastProgressionTime?: number;
};

// Timeline
const totalWidth: number = 2000;
const totalHeight: number = 200;
const timelineStartX: number = 100;
const timelineEndX: number = 1900;
const playLineHeight: number = 12;

// Sliding window
const swY: number = 70;
const swLineHeight: number = 4;
const swTimelineBarHeight: number = 12;
const swWidth: number = 900;
const slidingWindowOffsetBefore: number = 30;
const slidingWindowOffsetAfter: number = 60;
// Click area
const clickAreaHeight: number = 60;

const SessionTimeline = (props: Props) => {
  const {
    playTime,
    fps: propFps,
    seekToPlayTime,
    videoDuration,
    teamHome,
    teamAway,
    periods,
    events,
    ranges,
    timeMarkers,
    flags,
    initialGameState,
    gameStateTransitions,
    startTimeOffset,
    slidingWindow,
    lastLabeledTime,
    lastProgressionTime,
  } = props;
  const timeOffset = startTimeOffset || 0;
  const fps: number = propFps || 25;

  // Timeline bar vars
  const timelineWidth: number = timelineEndX - timelineStartX;
  const timelineY: number = slidingWindow ? 150 : 100;
  const playPerc: number = Math.max(Math.min((playTime - timeOffset) / videoDuration, 1), 0);
  const playLineX: number = timelineStartX + playPerc * timelineWidth;
  const lastLabeledLineHeight: number = 8;

  // Sliding window vars
  const swStartX: number = timelineStartX + playPerc * swWidth;
  const swEndX: number = timelineEndX - swWidth + playPerc * swWidth;
  const beforeAfterRatio: number = slidingWindowOffsetBefore / (slidingWindowOffsetBefore + slidingWindowOffsetAfter);

  const swTimelineBarY: number = timelineY - playLineHeight * 0.5 - swTimelineBarHeight;
  const swPlayLineX: number = Math.min(
    Math.max(
      swStartX +
        (slidingWindowOffsetBefore / (slidingWindowOffsetBefore + slidingWindowOffsetAfter)) *
          (timelineWidth - swWidth),
      swStartX
    ),
    swEndX
  );

  const [hoverTime, setHoverTime] = useState<number | undefined>(undefined);
  const [timeIndicatorPosition, setTimeIndicatorPosition] = useState<Coordinate | undefined>(undefined);

  const getTimelineX = useCallback(
    (ts: number) => {
      return Math.max(
        timelineStartX,
        Math.min(timelineEndX, timelineStartX + ((ts - timeOffset) / videoDuration) * timelineWidth)
      );
    },
    [timeOffset, timelineWidth, videoDuration]
  );

  const swLeftX: number = getTimelineX(playTime - slidingWindowOffsetBefore);
  const swRightX: number = getTimelineX(playTime + slidingWindowOffsetAfter);

  const getSlidingX = useCallback(
    (ts: number) => {
      return Math.max(
        swStartX,
        Math.min(
          swEndX,
          swPlayLineX +
            ((ts - playTime) / (slidingWindowOffsetBefore + slidingWindowOffsetAfter)) * (timelineWidth - swWidth)
        )
      );
    },
    [playTime, swEndX, swPlayLineX, swStartX, timelineWidth]
  );

  const swProgressionLineX: number = lastProgressionTime ? getSlidingX(lastProgressionTime) : swPlayLineX;

  const onClickTimeline = useCallback(
    (event: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      const currentTargetRect: DOMRect = event.currentTarget.getBoundingClientRect();
      const eventOffsetX: number = event.pageX - currentTargetRect.left;
      const timelinePercentage: number = eventOffsetX / currentTargetRect.width;
      const newTime: number = timeOffset + timelinePercentage * videoDuration;
      seekToPlayTime(newTime, false);
    },
    [seekToPlayTime, timeOffset, videoDuration]
  );

  const onClickSlidingWindow = useCallback(
    (event: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      const currentTargetRect: DOMRect = event.currentTarget.getBoundingClientRect();
      const eventOffsetX: number = event.pageX - currentTargetRect.left;
      const timelinePercentage: number = eventOffsetX / currentTargetRect.width;
      // Seek to right timeline window position
      const secsOffset: number =
        timelinePercentage < beforeAfterRatio
          ? slidingWindowOffsetBefore * -1 * ((beforeAfterRatio - timelinePercentage) / beforeAfterRatio)
          : slidingWindowOffsetAfter *
            (1 - (1 - beforeAfterRatio - (timelinePercentage - beforeAfterRatio)) / (1 - beforeAfterRatio));
      seekToPlayTime(playTime + secsOffset, true);
      setHoverTime(playTime + secsOffset + secsOffset);
    },
    [beforeAfterRatio, playTime, seekToPlayTime]
  );

  const onMoveTimeline = useCallback(
    (event: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      const currentTargetRect: DOMRect = event.currentTarget.getBoundingClientRect();
      const eventOffsetX: number = event.pageX - currentTargetRect.left;
      const timelinePercentage: number = eventOffsetX / currentTargetRect.width;
      const newTime: number = timeOffset + timelinePercentage * videoDuration;
      setHoverTime(newTime);
      const newPos: Coordinate = {
        x: timelineStartX + timelinePercentage * timelineWidth,
        y: timelineY - clickAreaHeight * 0.5,
      };
      setTimeIndicatorPosition(newPos);
    },
    [timeOffset, videoDuration, timelineWidth, timelineY]
  );

  const seekToFrame = useCallback(
    (frame: number) => {
      seekToPlayTime(frame / fps, true);
    },
    [fps, seekToPlayTime]
  );

  const onMoveSlidingWindow = useCallback(
    (event: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      const currentTargetRect: DOMRect = event.currentTarget.getBoundingClientRect();
      const eventOffsetX: number = event.pageX - currentTargetRect.left;
      const timelinePercentage: number = eventOffsetX / currentTargetRect.width;
      // Seek to right timeline window position
      const secsOffset: number =
        timelinePercentage < beforeAfterRatio
          ? slidingWindowOffsetBefore * -1 * ((beforeAfterRatio - timelinePercentage) / beforeAfterRatio)
          : slidingWindowOffsetAfter *
            (1 - (1 - beforeAfterRatio - (timelinePercentage - beforeAfterRatio)) / (1 - beforeAfterRatio));

      setHoverTime(playTime + secsOffset);

      const newPos: Coordinate = {
        x: swStartX + timelinePercentage * swWidth,
        y: swY - clickAreaHeight * 0.5,
      };
      setTimeIndicatorPosition(newPos);
    },
    [beforeAfterRatio, playTime, swStartX]
  );

  const resetTimeIndicator = useCallback((event: React.MouseEvent<SVGRectElement, MouseEvent>) => {
    setHoverTime(undefined);
    setTimeIndicatorPosition(undefined);
  }, []);

  const labeledUntilOverlay = useMemo(() => {
    // We need a last labeled time to begin with.
    if (!lastLabeledTime) return <></>;

    // Transform transition frame ids to time.
    const transitions: number[] = gameStateTransitions?.map((frameId: number) => frameId / fps) || [];
    transitions.push(lastLabeledTime);

    // If we do have game state transitions, create rects here.
    let prevTime: number | undefined = undefined;
    let currentState: boolean = initialGameState || true;
    let i: number = 0;
    const stateRects: JSX.Element[] = transitions.map((transitionTime: number) => {
      const startPerc: number = Math.max(Math.min(((prevTime || 0) - timeOffset) / videoDuration, 1), 0);
      const startRectX: number = timelineStartX + startPerc * timelineWidth;
      const endPerc: number = Math.max(Math.min((transitionTime - timeOffset) / videoDuration, 1), 0);
      const endRectX: number = timelineStartX + endPerc * timelineWidth;

      const newRect: JSX.Element = (
        <rect
          key={i++}
          x={startRectX}
          width={endRectX - startRectX}
          y={timelineY - lastLabeledLineHeight / 2}
          height={lastLabeledLineHeight}
          fill={currentState ? '#a0e290' : '#bb9292'}
        />
      );

      currentState = !currentState;
      prevTime = transitionTime;

      return newRect;
    });

    return stateRects;
  }, [
    fps,
    gameStateTransitions,
    initialGameState,
    lastLabeledTime,
    timeOffset,
    timelineWidth,
    timelineY,
    videoDuration,
  ]);

  const periodGraphics = useMemo(() => {
    return !periods
      ? []
      : periods.map((period: Period) => {
          return (
            <React.Fragment key={period.id}>
              <SessionTimelinePeriod
                period={period}
                startX={period.startTime ? getTimelineX(timeSpanToSeconds(period.startTime)) : undefined}
                endX={period.endTime ? getTimelineX(timeSpanToSeconds(period.endTime)) : undefined}
                timelineY={timelineY}
                graphicWidth={20}
                graphicHeight={40}
              />
            </React.Fragment>
          );
        });
  }, [getTimelineX, periods, timelineY]);

  const eventGraphics = useMemo(() => {
    return !events
      ? []
      : events.map((event: SessionEvent) => {
          return (
            <React.Fragment key={event.id}>
              <SessionTimelineEvent
                event={event}
                x={getTimelineX(timeSpanToSeconds(event.timestamp))}
                timelineY={timelineY}
                graphicWidth={32}
                graphicHeight={32}
                isHomeTeam={!!teamHome && event.teamId === teamHome.id}
              />
            </React.Fragment>
          );
        });
  }, [events, getTimelineX, teamHome, timelineY]);

  let id: number = 0;
  const allRangeGraphics = ranges ? (
    ranges?.map((rangeElement: RangeGraphics) => {
      return rangeElement.ranges
        .filter((range: FrameRange) => {
          const startX: number = getSlidingX(range.start_frame / fps);
          const endX: number = range.end_frame ? getSlidingX(range.end_frame / fps) : swEndX;
          return !(startX > swEndX || endX < swStartX);
        })
        .map((range: FrameRange) => {
          const startX: number = getSlidingX(range.start_frame / fps);
          const endX: number = range.end_frame ? getSlidingX(range.end_frame / fps) : swEndX;

          let gfx = <></>;
          if (rangeElement.rangeType === RangeType.Banner) {
            gfx = (
              <React.Fragment key={id++}>
                <line
                  x1={startX}
                  x2={endX}
                  y1={swY - 12}
                  y2={swY - 12}
                  stroke={rangeElement.color}
                  strokeWidth={8}
                  z={5000}
                />
                <line x1={startX} x2={startX} y1={swY} y2={swY - 18} stroke={rangeElement.color} strokeWidth={2} />
                <line x1={endX} x2={endX} y1={swY} y2={swY - 18} stroke={rangeElement.color} strokeWidth={2} />
              </React.Fragment>
            );
          }
          return gfx;
        });
    })
  ) : (
    <></>
  );

  const allTimeMarkerGraphics = timeMarkers ? (
    timeMarkers.map((markers: MarkerGraphics) => {
      return markers.timestamps
        .filter((ts: number) => {
          const markerX: number = getSlidingX(ts);
          return !(markerX < swStartX || markerX > swEndX);
        })
        .map((ts: number) => {
          const markerX: number = getSlidingX(ts);
          const gfx = (
            <React.Fragment key={id++}>
              <line x1={markerX} x2={markerX} y1={swY - 18} y2={swY + 6} stroke="#000" strokeWidth={1} z={5100} />
              <circle cx={markerX} cy={swY - 21} r={6} fill={markers.color} stroke="#000" strokeWidth={1} z={5100} />
            </React.Fragment>
          );
          return gfx;
        });
    })
  ) : (
    <></>
  );

  const timeRangeGraphics = () => {
    // Draw a line for each second in the timeline
    const lines = [];
    const totalSeconds: number = slidingWindowOffsetBefore + slidingWindowOffsetAfter;
    const clusterSeconds: number = 5;
    for (let i: number = clusterSeconds; i <= totalSeconds; ++i) {
      const x: number = swStartX + (i - (playTime % clusterSeconds)) * (swWidth / totalSeconds);
      // On every 5 seconds, add a bigger line
      if (i % clusterSeconds === 0) {
        lines.push(
          <line
            key={'sL' + i}
            x1={x}
            x2={x}
            y1={swY}
            y2={swY - swTimelineBarHeight * 1.5}
            stroke="#333"
            strokeWidth={2}
          />
        );
      } else {
        // Per second line
        lines.push(
          <line key={'sL' + i} x1={x} x2={x} y1={swY} y2={swY - swTimelineBarHeight} stroke="#666" strokeWidth={1} />
        );
      }
    }
    return lines;
  };

  const flagGraphics = flags ? (
    flags.map((flag: Flag) => {
      const tsStart: number = flag.start / fps;
      const tsEnd: number = flag.end / fps;
      return (
        <TimelineFlag
          key={flag.id}
          xStart={getTimelineX(tsStart)}
          xEnd={getTimelineX(tsEnd)}
          swXStart={getSlidingX(tsStart)}
          swXEnd={getSlidingX(tsEnd)}
          timelineY={timelineY}
          swY={swY}
          flag={flag}
          seekToFrame={seekToFrame}
          showInSw={!(tsEnd < playTime - slidingWindowOffsetBefore || tsStart > playTime + slidingWindowOffsetAfter)}
        />
      );
    })
  ) : (
    <></>
  );

  const slidingWindowGraphic = (
    <React.Fragment>
      {/* Sliding window bar */}
      <line x1={swStartX} x2={swEndX} y1={swY} y2={swY} stroke="#000" strokeWidth={swLineHeight} />

      {/* Window indication lines */}
      <line
        x1={swStartX}
        x2={swLeftX}
        y1={swY + swLineHeight * 0.5}
        y2={swTimelineBarY}
        stroke="#33578d"
        strokeWidth={2}
        strokeDasharray="2 3"
      />
      <line
        x1={swEndX}
        x2={swRightX}
        y1={swY + swLineHeight * 0.5}
        y2={swTimelineBarY}
        stroke="#33578d"
        strokeWidth={2}
        strokeDasharray="2 3"
      />
      <line
        x1={swLeftX}
        x2={swLeftX}
        y1={swTimelineBarY}
        y2={swTimelineBarY + swTimelineBarHeight}
        stroke="#33578d"
        strokeWidth={4}
      />
      <line
        x1={swRightX}
        x2={swRightX}
        y1={swTimelineBarY}
        y2={swTimelineBarY + swTimelineBarHeight}
        stroke="#33578d"
        strokeWidth={4}
      />

      {/*+ and - time texts */}
      <text x={swStartX} y={swY - 24} fontSize={16} dominantBaseline="middle" textAnchor="middle">
        - {slidingWindowOffsetBefore}s
      </text>
      <text x={swEndX} y={swY - 24} fontSize={16} dominantBaseline="middle" textAnchor="middle">
        + {slidingWindowOffsetAfter}s
      </text>

      {/* Sliding Window Progression Visualization */}
      <React.Fragment>
        <line x1={swStartX} x2={swProgressionLineX} y1={swY - 8} y2={swY - 8} stroke="#adaceb" strokeWidth={8} />
        <line x1={swProgressionLineX} x2={swProgressionLineX} y1={swY - 16} y2={swY} stroke="#0e0d4d" strokeWidth={8} />
      </React.Fragment>

      {/* Time range indicators */}
      {timeRangeGraphics()}

      {/* Ranges Visualization */}
      {allRangeGraphics}

      {/* Time marker Visualization */}
      {allTimeMarkerGraphics}

      {/* Flag display */}
      {flagGraphics}

      {/* Playtime Indicator */}
      <rect
        x={swPlayLineX - 3}
        width={6}
        y={swY - 22}
        height={32}
        fill="#135bb9"
        stroke="#135bb9"
        strokeWidth={3}
        opacity={1}
      />
    </React.Fragment>
  );

  return videoDuration === 0 ? (
    <></>
  ) : (
    <svg
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      viewBox={'0 0 ' + totalWidth + ' ' + totalHeight}
      xmlns="http://www.w3.org/2000/svg"
      style={{ backgroundColor: '#ccf1' }}
    >
      {/* Timeline bar */}
      <line
        x1={timelineStartX}
        x2={timelineEndX}
        y1={timelineY}
        y2={timelineY}
        stroke="#000"
        strokeWidth={playLineHeight}
      />

      {/* Team labels left of timeline */}
      <text x={32} y={timelineY - 36} fontSize={16}>
        {teamHome?.name}
      </text>
      <text x={32} y={timelineY + 48} fontSize={16}>
        {teamAway?.name}
      </text>

      {/* Video start & end labels */}
      <text
        x={timelineStartX}
        y={timelineY + 24}
        fontSize={16}
        dominantBaseline="middle"
        textAnchor="middle"
        textLength={60}
      >
        {secondsToHms(timeOffset)}
      </text>
      <text
        x={timelineEndX}
        y={timelineY + 24}
        fontSize={16}
        dominantBaseline="middle"
        textAnchor="middle"
        textLength={60}
      >
        {secondsToHms(timeOffset + videoDuration)}
      </text>

      {/*Labeled until overlay */}
      {labeledUntilOverlay}

      {/* Periods */}
      {periodGraphics}

      {/* Events */}
      {eventGraphics}

      {/* Sliding window */}
      {slidingWindow && slidingWindowGraphic}

      {/* Current time label */}
      <rect x={playLineX - 40} y={timelineY + 18} fill="#95d8ff" width={80} height={20} />
      <text
        x={playLineX}
        y={timelineY + 28}
        fontSize={16}
        dominantBaseline="middle"
        textAnchor="middle"
        textLength={60}
        fontWeight="bold"
      >
        {secondsToHms(playTime)}
      </text>

      {/* Video progress overlay */}
      <rect
        x={timelineStartX}
        width={playLineX - timelineStartX}
        y={timelineY - playLineHeight / 2}
        height={playLineHeight}
        fill="#0000"
        opacity={1}
        stroke="#135bb9"
        strokeWidth={6}
      />

      {/* Play time indicator */}
      <rect
        x={playLineX - 4}
        width={8}
        y={timelineY - 16}
        height={32}
        fill="#135bb9"
        stroke="#135bb9"
        strokeWidth={3}
      />

      {/*Hover Time at Mouse position*/}
      {timeIndicatorPosition && hoverTime && (
        <svg xmlns="http://www.w3.org/2000/svg">
          <g>
            <rect
              x={timeIndicatorPosition.x - 40}
              y={timeIndicatorPosition.y + 20}
              fill="#000b"
              width={80}
              height={20}
            />
            <line
              x1={timeIndicatorPosition.x}
              x2={timeIndicatorPosition.x}
              y1={timeIndicatorPosition.y + 5}
              y2={timeIndicatorPosition.y + 25}
              stroke="#000"
              strokeWidth={4}
            />
            <text
              x={timeIndicatorPosition.x}
              y={timeIndicatorPosition.y + 32}
              fill="#95d8ff"
              textLength={60}
              textAnchor="middle"
              fontSize={16}
              dominantBaseline="middle"
              fontWeight="bold"
            >
              {secondsToHms(hoverTime)}
            </text>
          </g>
        </svg>
      )}

      {/* Timeline Click area */}
      <rect
        x={timelineStartX}
        y={timelineY - clickAreaHeight * 0.5}
        width={timelineWidth}
        height={clickAreaHeight}
        opacity={0}
        onClick={onClickTimeline}
        onMouseMove={onMoveTimeline}
        onMouseLeave={resetTimeIndicator}
        cursor="pointer"
      />

      {/* Sliding Window Click area */}
      {slidingWindow && (
        <rect
          x={swStartX}
          y={swY - clickAreaHeight * 0.5}
          width={swEndX - swStartX}
          height={clickAreaHeight}
          opacity={0}
          onClick={onClickSlidingWindow}
          onMouseMove={onMoveSlidingWindow}
          onMouseLeave={resetTimeIndicator}
          cursor="pointer"
        />
      )}
    </svg>
  );
};

export default SessionTimeline;
